import React from 'react';

export function formatDateTimeLocal(isoString) {
  if (!isoString) return 'Not scheduled';
  const parsed = new Date(isoString);
  if (Number.isNaN(parsed.getTime())) return isoString;
  return parsed.toLocaleString();
}

export function toUtcIsoFromLocalInput(value) {
  return value ? new Date(value).toISOString() : '';
}

export function formatCommercialRole(role) {
  if (role === 'Tenant Admin') return 'Company Admin';
  if (role === 'Tenant Member') return 'Company Member';
  return role || 'Company Member';
}

function normalizeSearchText(value) {
  return String(value ?? '').trim().toLowerCase();
}

function getComparableValue(value) {
  if (typeof value === 'boolean') return value ? 1 : 0;
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') return value;
  return String(value).toLowerCase();
}

export function filterAndSortRows(rows, {
  search = '',
  searchKeys = [],
  filters = {},
  filterAccessors = {},
  sortKey = '',
  sortDirection = 'asc',
}) {
  const normalizedSearch = normalizeSearchText(search);

  const filteredRows = rows.filter(row => {
    const matchesSearch = !normalizedSearch || searchKeys.some(key => normalizeSearchText(row[key]).includes(normalizedSearch));
    if (!matchesSearch) return false;

    return Object.entries(filters).every(([key, selectedValue]) => {
      if (!selectedValue) return true;
      const accessor = filterAccessors[key];
      const rowValue = accessor ? accessor(row) : row[key];
      return String(rowValue ?? '') === String(selectedValue);
    });
  });

  if (!sortKey) return filteredRows;

  return [...filteredRows].sort((left, right) => {
    const leftValue = getComparableValue(left[sortKey]);
    const rightValue = getComparableValue(right[sortKey]);
    if (leftValue < rightValue) return sortDirection === 'asc' ? -1 : 1;
    if (leftValue > rightValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });
}

export function SummaryCard({ label, value, tone = 'default' }) {
  return (
    <article className={`commercial-summary-card ${tone !== 'default' ? `is-${tone}` : ''}`}>
      <p className="commercial-summary-card__label">{label}</p>
      <p className="commercial-summary-card__value">{value}</p>
    </article>
  );
}

export function SectionFeedback({ error, success }) {
  return (
    <>
      {error ? <div className="commercial-error" style={{ marginTop: 12 }}>{error}</div> : null}
      {success ? <div className="commercial-success" style={{ marginTop: 12 }}>{success}</div> : null}
    </>
  );
}

export function DataTable({ columns, rows, emptyMessage, onRowSelect, selectedRowId }) {
  return (
    <div className="commercial-data-table-wrap">
      <table className="commercial-data-table">
        <thead>
          <tr>
            {columns.map(column => (
              <th key={column.key}>{column.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length > 0 ? rows.map((row, index) => (
            <tr
              key={row.id || `${index}-${columns[0]?.key || 'row'}`}
              className={onRowSelect ? `commercial-data-table__row ${selectedRowId === row.id ? 'is-selected' : ''}` : undefined}
              onClick={onRowSelect ? () => onRowSelect(row) : undefined}
            >
              {columns.map(column => (
                <td key={column.key}>{column.render ? column.render(row) : row[column.key] || '-'}</td>
              ))}
            </tr>
          )) : (
            <tr>
              <td colSpan={columns.length} className="commercial-data-table__empty">{emptyMessage}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export function TableControls({
  search,
  onSearchChange,
  searchPlaceholder = 'Search',
  sortKey,
  onSortKeyChange,
  sortOptions = [],
  sortDirection,
  onSortDirectionChange,
  filters = [],
  filterValues = {},
  onFilterChange,
  onClear,
  actions,
}) {
  return (
    <div className="commercial-table-controls">
      <input
        className="commercial-table-controls__search"
        type="search"
        value={search}
        onChange={event => onSearchChange?.(event.target.value)}
        placeholder={searchPlaceholder}
      />
      {filters.map(filter => (
        <label key={filter.key} className="commercial-table-controls__select">
          <span>{filter.label}</span>
          <select value={filterValues[filter.key] || ''} onChange={event => onFilterChange?.(filter.key, event.target.value)}>
            <option value="">{filter.emptyLabel || `All ${filter.label}`}</option>
            {filter.options.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>
      ))}
      {sortOptions.length ? (
        <label className="commercial-table-controls__select">
          <span>Sort By</span>
          <select value={sortKey} onChange={event => onSortKeyChange?.(event.target.value)}>
            {sortOptions.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>
      ) : null}
      {sortOptions.length ? (
        <button type="button" className="commercial-table-controls__ghost" onClick={() => onSortDirectionChange?.(sortDirection === 'asc' ? 'desc' : 'asc')}>
          {sortDirection === 'asc' ? 'Ascending' : 'Descending'}
        </button>
      ) : null}
      <button type="button" className="commercial-table-controls__ghost" onClick={onClear}>Clear</button>
      {actions}
    </div>
  );
}

export function TenantOverviewPanel({ tenant, summary }) {
  if (!tenant) return null;

  return (
    <section className="commercial-panel commercial-panel--subtle">
      <p className="commercial-panel__eyebrow">Company</p>
      <h2 className="commercial-panel__title">{tenant.companyName}</h2>
      <p className="commercial-panel__copy">
        Your company workspace is active with timezone <strong>{tenant.timezone}</strong> and language <strong>{tenant.locale}</strong>.
      </p>
      <div className="commercial-checklist">
        <div className="commercial-checklist__item">
          <span className="commercial-checklist__dot">1</span>
          <span>Template: {(tenant.activeTemplateType || '').replaceAll('_', ' ') || 'Not selected'}</span>
        </div>
        <div className="commercial-checklist__item">
          <span className="commercial-checklist__dot">2</span>
          <span>Domain settings: {tenant.companyDomain ? `${tenant.companyDomain}${tenant.restrictSignupToCompanyDomain ? ' (matching email required)' : ''}` : 'No company domain added yet'}</span>
        </div>
        {summary ? (
          <div className="commercial-checklist__item">
            <span className="commercial-checklist__dot">3</span>
            <span>{summary.openWorkOrderCount} open work orders and {summary.dispatchableTechnicianCount} dispatch-ready technicians.</span>
          </div>
        ) : null}
      </div>
    </section>
  );
}
